/**
 * roles.js — Source de vérité unique pour les rôles.
 *
 * Chaque entrée de ROLES contient l'identifiant technique (id) et,
 * pour les rôles avec une représentation UI, le label, la couleur et
 * les permissions affichées dans le panneau Membres.
 *
 * Accès au string Auth0 : ROLES.ADMIN.id  → 'mobiuspack-admin'
 * Accès au label UI      : ROLES.ADMIN.label → 'Admin'
 */
export const ROLES = {
  ADMIN: {
    id: 'mobiuspack-admin', //key auth0
    label: 'Admin',
    color: '#0072e4',
    permissions: ["Configuration de l'ensemble du système", 'Gestion membres & accès', 'Tous flux & tous sites'],
  },
  ACCOUNT_MANAGER:  { id: 'account-manager' },
  PARTNER:          { id: 'partner' },
  PROJECT_MANAGER:  { id: 'project-manager' },
  GROUP_MANAGER: {
    id: 'group-manager',
    label: 'Responsable de région',
    color: '#7c3aed',
    permissions: ['Vue réseau (lecture)', 'Expéditions (région)', 'Flux configurés (lecture)'],
  },
  WAREHOUSE_MANAGER: {
    id: 'warehouse-manager',
    label: 'Responsable entrepôt',
    color: '#0891b2',
    permissions: ['Opérations entrepôt', 'Expéditions sortantes'],
  },
  STORE_MANAGER: {
    id: 'store-manager',
    label: 'Responsable magasin',
    color: '#059669',
    permissions: ['Vue magasin'],
  },
  PROSPECT: { id: 'prospect' },
};

/** Tous les ids de roles authentifiés avec accès au menu (hors prospect). */
export const ACCESS_MENU_ROLES = [
  ROLES.ADMIN.id,
  ROLES.ACCOUNT_MANAGER.id,
  ROLES.PARTNER.id,
  ROLES.PROJECT_MANAGER.id,
  ROLES.GROUP_MANAGER.id,
  ROLES.WAREHOUSE_MANAGER.id,
  ROLES.STORE_MANAGER.id,
];

export const ALL_ROLES = Object.values(ROLES).map(r => r.id);

/** Alias — utilisé dans routes.config et routeBuilder. */
export const ROLE_WITH_MENU_ACCESS = ACCESS_MENU_ROLES;

/** Rôles avec accès à toutes les pages client. */
export const ALL_PAGE_ACCESS_ROLES = [ROLES.ADMIN.id, ROLES.ACCOUNT_MANAGER.id, ROLES.PROJECT_MANAGER.id];

/** Rôles partenaire. */
export const PARTNER_ROLES = [ROLES.PARTNER.id, ROLES.ADMIN.id];

/** Rôles admin uniquement. */
export const ADMIN_ROLES = [ROLES.ADMIN.id];

/**
 * Helper : vérifie si l'utilisateur possède au moins un des rôles requis.
 * @param {string[]} userRoles   - rôles de l'utilisateur connecté (strings Auth0)
 * @param {string[]} allowedRoles - rôles autorisés pour la ressource
 */
export const hasAnyRole = (userRoles = [], allowedRoles = []) =>
  allowedRoles.some((role) => userRoles.includes(role));

/**
 * Rang hiérarchique des rôles, du plus élevé (1) au plus bas — utilisé pour empêcher un
 * inviteur (project-manager, group-manager, ...) de proposer un rôle plus élevé que le
 * sien lors d'une invitation ou d'un changement de rôle (cf. MembersContainer).
 * PARTNER (accès compte client partenaire) et PROSPECT sont au rang Account Manager :
 * seuls Admin/Account Manager peuvent les proposer, jamais project/group/warehouse/store manager.
 */
export const ROLE_RANK = {
  [ROLES.ADMIN.id]: 1,
  [ROLES.ACCOUNT_MANAGER.id]: 2,
  [ROLES.PARTNER.id]: 2,
  [ROLES.PROSPECT.id]: 2,
  [ROLES.PROJECT_MANAGER.id]: 3,
  [ROLES.GROUP_MANAGER.id]: 4,
  [ROLES.WAREHOUSE_MANAGER.id]: 5,
  [ROLES.STORE_MANAGER.id]: 5,
};

/** Rang le plus élevé (valeur la plus basse) parmi les rôles donnés, ou null si aucun rôle rangé. */
export const getOwnRank = (userRoles = []) => {
  const ranks = userRoles.map((role) => ROLE_RANK[role]).filter((rank) => rank !== undefined);
  return ranks.length > 0 ? Math.min(...ranks) : null;
};
